import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Input } from 'reactstrap';
import toast from 'react-hot-toast';
import { LoadingIndicator } from '../helpers/LoadingIndicator';
import {
  useCreateDashboardMutation,
  useGetDashboardsQuery,
} from '../services/edgeApi';

export const DashboardsPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: dashboards = [], isLoading, error } = useGetDashboardsQuery();
  const [createDashboard, { isLoading: isCreating }] = useCreateDashboardMutation();

  const [newDashboardName, setNewDashboardName] = useState('');

  const sortedDashboards = useMemo(
    () => [...dashboards].sort((a, b) => a.id - b.id),
    [dashboards],
  );

  const onCreateDashboard = async () => {
    const name = newDashboardName.trim();
    if (!name) {
      toast.error('Enter a dashboard name first.');
      return;
    }

    try {
      const created = await createDashboard({
        name,
        description: null,
      }).unwrap();
      setNewDashboardName('');
      toast.success('Dashboard created');
      navigate(`/dashboards/${created.id}`);
    } catch {
      toast.error('Unable to create dashboard.');
    }
  };

  if (isLoading) {
    return (
      <div className="container-fluid py-3 d-flex justify-content-center align-items-center h-100">
        <LoadingIndicator />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid py-3">
        <div className="alert alert-danger mb-0">Unable to load dashboards.</div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-3 d-flex flex-column gap-4 overflow-auto h-100">
      <div className="d-flex justify-content-between align-items-start gap-4 flex-wrap">
        <div>
          <h2 className="mb-1">Dashboards</h2>
          <p className="text-muted mb-0">
            Select a dashboard or create a new one.
          </p>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body d-flex gap-2 align-items-center flex-wrap">
          <Input
            placeholder="New dashboard name"
            value={newDashboardName}
            onChange={(event) => setNewDashboardName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                onCreateDashboard();
              }
            }}
            style={{ minWidth: 280, maxWidth: 460 }}
          />
          <Button color="primary" onClick={onCreateDashboard} disabled={isCreating}>
            Create Dashboard
          </Button>
        </div>
      </div>

      {sortedDashboards.length === 0 ? (
        <div className="border rounded bg-light p-4 text-center text-muted">
          No dashboards yet. Create your first dashboard above.
        </div>
      ) : (
        <div className="row g-3">
          {sortedDashboards.map((dashboard) => (
            <div className="col-12 col-md-6 col-xl-4" key={dashboard.id}>
              <Link
                to={`/dashboards/${dashboard.id}`}
                className="card shadow-sm h-100 text-decoration-none"
              >
                <div className="card-body d-flex flex-column gap-2">
                  <div className="d-flex justify-content-between align-items-center gap-3">
                    <h5 className="mb-0 text-dark">{dashboard.name || `Dashboard ${dashboard.id}`}</h5>
                    <span className="badge bg-light text-dark border">#{dashboard.id}</span>
                  </div>
                  <div className="text-muted small">
                    {dashboard.widgets?.length || 0} widget{(dashboard.widgets?.length || 0) === 1 ? '' : 's'}
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
